import { CustomButton, CustomInput, CustomModal, If } from 'components';
import InputWithPrefix from 'components/input-with-prefix/InputWithPrefix';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import React, { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VoidFn } from 'types/baseTypes';
import { ArrowRightIcon, CrossBoundaryIcon } from 'assets/icons';
import Divider from 'components/divider/Divider';
import { useForm } from 'react-hook-form';
import { CreateCustomerModalTypes } from 'constants/CustomerConstants';
import { CustomerItem } from 'containers/invoices/invoices.model';
import { isEmailEndingValid, removeUSCountryCode, throttle } from 'utils/commonUtils';
import Lottie from 'lottie-react';
import { LoadingSpinnerAnimation } from 'assets/animations';
import { any, string, z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { REGEX } from 'constants/common';
import { InputType } from 'components/custom-input.tsx/CustomInput';
import { ButtonType, FormButtonType } from 'components/custom-button/CustomButton';
import { logAnalyticEvent } from 'utils/analytics';
import { JobsCleverTapEvents } from 'containers/jobs/events';
import { getPhoneNumberDigits, isValidPhoneNumber } from 'utils/phoneNumberUtils';

interface CustomerData extends Omit<CustomerItem, 'merchantId' | 'billingAddress'> {
  merchantId?: string;
  companyName?: string;
  referenceNumber?: string;
  note?: string;
}

interface CreateCustomerProps {
  open: boolean;
  onClose: VoidFn;
  panelType?: CreateCustomerModalTypes;
  customerData?: CustomerData | null;
  customerDataFetchLoading?: boolean;
  customerDataFetchSuccess?: boolean;
  defaultName?: string;
  onAddCustomer?: (customer) => void;
  isLoading?: boolean;
}

const lineItemFormSchema = z.object({
  name: string().optional(),
  email: string().optional(),
  phoneNumber: any(),
  lastName: string().optional(),
  companyName: string().optional(),
  referenceNumber: string().optional(),
  note: string().optional()
});

const CreateCustomer: FC<CreateCustomerProps> = ({
  open,
  onClose,
  panelType = CreateCustomerModalTypes.CREATE_CUSTOMER,
  customerData = null,
  customerDataFetchLoading = false,
  customerDataFetchSuccess = false,
  onAddCustomer,
  defaultName = '',
  isLoading = false
}) => {
  const activeTeam = useAppSelector(selectCurrentTeam);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [phoneOrEmailError, setPhoneOrEmailError] = useState('');
  const lastSubmittedEditRef = useRef<{
    customerId: string;
    name: string;
    email: string;
    lastName: string;
    phoneNumber: string;
    phoneDisplay: string;
    companyName: string;
    referenceNumber: string;
    note: string;
  } | null>(null);

  let defaultFirstName = '';
  let defaultLastName = '';
  if (defaultName !== '') {
    const firstSplitIndex = defaultName.indexOf(' ');
    if (firstSplitIndex !== -1) {
      defaultFirstName = defaultName.substring(0, firstSplitIndex);
      defaultLastName = defaultName.substring(firstSplitIndex + 1);
    } else defaultFirstName = defaultName;
  }

  const { handleSubmit, reset, formState, setError, clearErrors, getValues, setValue } = useForm({
    defaultValues: useMemo(() => {
      return {
        name: customerData?.name || defaultFirstName || '',
        email: customerData?.email || '',
        // phoneNumber: getPhoneDefaultValue(),
        lastName: customerData?.lastName || defaultLastName || '',
        companyName: customerData?.companyName || '',
        referenceNumber: customerData?.referenceNumber || '',
        note: customerData?.note || ''
      };
    }, [customerData, customerDataFetchLoading]),
    resolver: zodResolver(lineItemFormSchema)
  });

  const { errors, isDirty } = formState;

  useEffect(() => {
    logAnalyticEvent(JobsCleverTapEvents.webCreateCustomerPageView);
  }, []);

  useEffect(() => {
    const isEdit = panelType === CreateCustomerModalTypes.EDIT_CUSTOMER;
    const customerId = customerData?.ID ?? customerData?.id ?? '';

    if (isEdit && lastSubmittedEditRef.current && lastSubmittedEditRef.current.customerId === customerId) {
      const saved = lastSubmittedEditRef.current;
      reset({
        name: saved.name,
        email: saved.email,
        lastName: saved.lastName,
        companyName: saved.companyName,
        referenceNumber: saved.referenceNumber,
        note: saved.note
      });
      setPhoneNumber(saved.phoneDisplay);
    } else {
      if (isEdit) lastSubmittedEditRef.current = null;
      reset({
        name: customerData?.name || defaultFirstName || '',
        email: customerData?.email || '',
        lastName: customerData?.lastName || defaultLastName || '',
        companyName: customerData?.companyName || '',
        referenceNumber: customerData?.referenceNumber || '',
        note: customerData?.note || ''
      });
      setPhoneNumber(removeUSCountryCode(customerData?.phoneNumber || ''));
    }
  }, [customerData, customerDataFetchLoading, panelType]);

  const panelTypeText =
    panelType === CreateCustomerModalTypes.CREATE_CUSTOMER ? 'Create New Customer' : 'Edit Customer Contact';

  const formSubmit = inputParams => {
    setPhoneOrEmailError('');
    setIsPhoneNumberValid(true);
    clearErrors(['name', 'lastName', 'email']);

    const nameValue = (inputParams.name || '').trim();
    const lastNameValue = (inputParams.lastName || '').trim();
    const phoneNumberDigits = phoneNumber ? getPhoneNumberDigits(phoneNumber) : '';
    const hasValidPhone = phoneNumberDigits.length > 0 && isValidPhoneNumber(phoneNumberDigits);
    const emailValue = (inputParams.email || '').trim();
    const hasValidEmail = emailValue.length > 0 && REGEX.email.test(emailValue) && isEmailEndingValid(emailValue);

    let hasErrors = false;

    if (nameValue.trim().length === 0) {
      setError('name', { type: 'manual', message: 'Please enter first name' });
      hasErrors = true;
    }
    if (nameValue.length > 0 && nameValue.length < 2) {
      setError('name', { type: 'manual', message: 'First name must be at least 2 characters' });
      hasErrors = true;
    }
    if (lastNameValue.length < 1) {
      setError('lastName', { type: 'manual', message: 'Please enter last name' });
      hasErrors = true;
    }

    const hasInvalidPhone = phoneNumberDigits.length > 0 && !isValidPhoneNumber(phoneNumberDigits);
    const hasInvalidEmail = emailValue.length > 0 && !(REGEX.email.test(emailValue) && isEmailEndingValid(emailValue));
    if (hasInvalidPhone && hasInvalidEmail) {
      setPhoneOrEmailError('Please enter a valid phone number or email');
      setIsEmailValid(true);
      hasErrors = true;
    } else if (hasInvalidPhone) {
      setIsPhoneNumberValid(false);
      setIsEmailValid(true);
      hasErrors = true;
    } else if (hasInvalidEmail) {
      setError('email', { type: 'manual', message: 'Please enter a valid email' });
      setIsEmailValid(false);
      hasErrors = true;
    } else if (!hasValidPhone && !hasValidEmail) {
      setPhoneOrEmailError('Please enter a valid phone number or email');
      setIsEmailValid(true);
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    const submittedData = {
      name: inputParams.name.trim(),
      email: inputParams.email.trim(),
      lastName: inputParams.lastName.trim(),
      phoneNumber: phoneNumberDigits,
      referenceNumber: inputParams.referenceNumber.trim(),
      note: inputParams.note.trim(),
      companyName: inputParams.companyName.trim()
    };

    onAddCustomer({
      ...(panelType === CreateCustomerModalTypes.EDIT_CUSTOMER && {
        customerId: customerData ? customerData?.ID : ''
      }),
      teamId: activeTeam.id.toString(),
      ...submittedData
    });

    if (panelType === CreateCustomerModalTypes.EDIT_CUSTOMER) {
      const customerId = customerData?.ID ?? customerData?.id ?? '';
      lastSubmittedEditRef.current = {
        customerId,
        ...submittedData,
        phoneDisplay: phoneNumber
      };
      reset({
        name: submittedData.name,
        email: submittedData.email,
        lastName: submittedData.lastName,
        companyName: submittedData.companyName,
        referenceNumber: submittedData.referenceNumber,
        note: submittedData.note
      });
      setPhoneNumber(phoneNumber);
    }
  };

  const getErrorMsg = error => {
    if (error?.type === 'invalid_type') {
      return 'Enter a valid input';
    }
    return error?.message;
  };

  const handleFirstNameChange = (value: string) => {
    setValue('name', value, { shouldValidate: true, shouldDirty: true });
    const trimmed = value.trim();
    if (trimmed.length > 0 && trimmed.length < 2) {
      setError('name', { type: 'manual', message: 'First name must be at least 2 characters' });
    } else {
      clearErrors('name');
    }
  };

  const handleLastNameChange = (value: string) => {
    setValue('lastName', value, { shouldValidate: true, shouldDirty: true });
    const trimmed = value.trim();
    if (trimmed.length < 1) {
      setError('lastName', { type: 'manual', message: 'Please enter last name' });
    } else {
      clearErrors('lastName');
    }
  };

  const handleChangePhoneNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event?.target?.value ?? '';
    if (value.trim().length === 0) {
      setPhoneNumber('');
      setPhoneOrEmailError('');
      setIsPhoneNumberValid(true);
      return;
    }
    if (REGEX.phoneNumberInput.test(value)) {
      setPhoneNumber(value);
      setPhoneOrEmailError('');
      const phoneNumberDigits = getPhoneNumberDigits(value);
      const isValid = phoneNumberDigits.length === 0 || isValidPhoneNumber(phoneNumberDigits);
      setIsPhoneNumberValid(isValid);
    }
  };

  const handleEmailChange = (value: string) => {
    setValue('email', value, { shouldValidate: true, shouldDirty: true });
    const isValid = value.length === 0 || (REGEX.email.test(value) && isEmailEndingValid(value));
    if (isValid) {
      clearErrors('email');
      setIsEmailValid(true);
      setPhoneOrEmailError('');
    } else {
      setError('email', {
        type: 'manual',
        message: 'Please enter valid email'
      });
      setIsEmailValid(false);
    }
  };

  const formSubmitThrottler = useCallback(throttle(formSubmit, 800), [customerData, phoneNumber]);

  const isAddButtonDisabled = () => {
    return (
      Object.keys(errors).length > 0 ||
      !isEmailValid ||
      !isPhoneNumberValid ||
      (panelType === CreateCustomerModalTypes.EDIT_CUSTOMER &&
        !isDirty &&
        phoneNumber === removeUSCountryCode(customerData?.phoneNumber || ''))
    );
  };

  return (
    <Fragment>
      <CustomModal open={open} backgroundColor="bg-primary" widthInPx={'607px'} enableOutsideClick={false}>
        <If condition={customerDataFetchLoading}>
          <div className="flex w-full items-center justify-center">
            <Lottie className="h-20 w-20" animationData={LoadingSpinnerAnimation} loop={true} />
          </div>
        </If>

        <If
          condition={
            (customerDataFetchSuccess && !customerDataFetchLoading) ||
            panelType === CreateCustomerModalTypes.CREATE_CUSTOMER
          }>
          <div className="flex w-full flex-col p-2">
            <div className="flex flex-row justify-end	">
              <CrossBoundaryIcon id="create-customer-close-button" className={'cursor-pointer'} onClick={onClose} />
            </div>
            <div className="mb-4 px-6">
              <div
                id={`${panelTypeText}-modal-heading`}
                className={'mb-5 font-lato text-xl font-bold text-headingGray'}>
                {panelTypeText}
              </div>
              <form className="w-full" onSubmit={handleSubmit(data => formSubmitThrottler(data))}>
                <div className=" mb-2 flex flex-wrap">
                  <div className="w-full pr-3 md:mb-0 md:w-1/2">
                    <CustomInput
                      id="customer-first-name"
                      type={InputType.TEXT}
                      placeholder="John"
                      value={getValues('name') || ''}
                      onChange={handleFirstNameChange}
                      label="First Name"
                      isMandatory={true}
                      error={getErrorMsg(errors.name)}
                      className="w-full appearance-none rounded bg-secondaryBtn py-3 px-4 
                  leading-tight text-gray-700 focus:bg-secondaryBtn focus:outline-none"
                    />
                  </div>
                  <div className="w-full pl-3 md:w-1/2">
                    <CustomInput
                      id="customer-last-name"
                      type={InputType.TEXT}
                      placeholder="Doe"
                      value={getValues('lastName') || ''}
                      onChange={handleLastNameChange}
                      label="Last Name"
                      isMandatory={true}
                      error={getErrorMsg(errors.lastName)}
                      className="w-full appearance-none rounded bg-secondaryBtn py-3 px-4 
                      leading-tight text-gray-700 focus:bg-secondaryBtn focus:outline-none"
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <div className="flex flex-wrap">
                    <div className="w-full pr-3 md:w-1/2">
                      <label
                        className="mb-2.5 block font-lato text-sbase font-semibold tracking-wide text-headingGray"
                        htmlFor="grid-phone-number">
                        Phone number
                      </label>
                      <InputWithPrefix
                        id="customer-phoneNumber"
                        type={InputType.TEXT}
                        placeholder="(000) 00-0000"
                        value={phoneNumber}
                        onChange={e => {
                          handleChangePhoneNumber(e);
                        }}
                        prefixString="+1"
                        containerStyles="w-full appearance-none rounded bg-secondaryBtn py-3 px-4 leading-tight"
                        className="appearance-none bg-secondaryBtn outline-none focus:outline-none"
                      />
                      <If condition={!isPhoneNumberValid}>
                        <div className="mt-1 text-sbase font-semibold text-error">
                          Please enter a valid phone number
                        </div>
                      </If>
                    </div>
                    <div className="w-full pl-3 md:w-1/2">
                      <CustomInput
                        id="customer-email"
                        type={InputType.TEXT}
                        placeholder="abc@example.com"
                        value={getValues('email') || ''}
                        onChange={handleEmailChange}
                        label="Email Id"
                        error={!isEmailValid ? 'Please enter a valid email id' : ''}
                        className="w-full appearance-none rounded bg-secondaryBtn py-3 px-4 
                      leading-tight text-gray-700 focus:bg-secondaryBtn focus:outline-none"
                      />
                    </div>
                  </div>

                  <If condition={!!phoneOrEmailError}>
                    <div className="mt-1 text-sbase font-semibold text-error">{phoneOrEmailError}</div>
                  </If>
                </div>

                <div className=" mb-5 flex flex-wrap">
                  <div className="w-full">
                    <CustomInput
                      id="customer-company"
                      type={InputType.TEXT}
                      placeholder="Customer company name"
                      value={getValues('companyName') || ''}
                      onChange={value => {
                        setValue('companyName', value, { shouldValidate: true, shouldDirty: true });
                      }}
                      label="Company name"
                      className="w-full appearance-none rounded bg-secondaryBtn py-3 px-4 
                      leading-tight text-gray-700 focus:bg-secondaryBtn focus:outline-none"
                    />
                  </div>
                </div>

                <Divider variant="fullWidth" className="border-borderGray" />

                <div className={'my-4 font-lato text-base font-bold text-headingGray'}>More details</div>

                <div className=" mb-5 flex flex-wrap">
                  <div className="w-full">
                    <CustomInput
                      id="customer-reference-number"
                      type={InputType.TEXT}
                      placeholder="eg. Cust123"
                      value={getValues('referenceNumber') || ''}
                      onChange={value => {
                        setValue('referenceNumber', value, { shouldValidate: true, shouldDirty: true });
                      }}
                      label="Customer reference ID"
                      className="w-full appearance-none rounded bg-secondaryBtn py-3 px-4 
                      leading-tight text-gray-700 focus:bg-secondaryBtn focus:outline-none"
                    />
                  </div>
                </div>
                <div className=" mb-5 flex flex-wrap">
                  <div className="w-full">
                    <CustomInput
                      id="customer-note"
                      type={InputType.TEXT}
                      placeholder="Add a note for your team"
                      value={getValues('note') || ''}
                      onChange={value => {
                        setValue('note', value, { shouldValidate: true, shouldDirty: true });
                      }}
                      label="Customer note"
                      className="w-full appearance-none rounded bg-secondaryBtn py-3 px-4 
                      leading-tight text-gray-700 focus:bg-secondaryBtn focus:outline-none"
                    />
                  </div>
                </div>

                {panelType === CreateCustomerModalTypes.EDIT_CUSTOMER && (
                  <div
                    className="flex cursor-pointer flex-row items-center"
                    onClick={() => {
                      window.open(`/customers/${activeTeam?.id}/${customerData.ID}/customer`, '_blank');
                    }}>
                    <div id="view-full-profile" className={'text-base font-semibold text-secondary'}>
                      View full profile
                    </div>
                    <div className={'ml-1'}>
                      <ArrowRightIcon />
                    </div>
                  </div>
                )}

                <div className={'mb-7 flex w-full flex-1 flex-row justify-end'}>
                  <CustomButton
                    id="create/edit-customer-cancel-button"
                    type={ButtonType.SECONDARY}
                    className="w-24"
                    onClick={onClose}>
                    <div className="font-semibold">Cancel</div>
                  </CustomButton>

                  <CustomButton
                    isLoading={isLoading}
                    id="create/update-customer-button"
                    className=" ml-5 w-24"
                    formType={FormButtonType.submit}>
                    <div className="text-base font-semibold">
                      {panelType === CreateCustomerModalTypes.CREATE_CUSTOMER ? 'Create' : 'Update'}
                    </div>
                  </CustomButton>
                </div>
              </form>
            </div>
          </div>
        </If>
      </CustomModal>
    </Fragment>
  );
};

export default CreateCustomer;
